



























import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  reactive,
} from '@vue/composition-api';
import boardEntryApi from '@/apis/board_entry';
import { BoardUnreadNotificationResponse } from '@/models/apis/settouMessageBoard/settouMessageBoardResponse';
import { fuzzyTimeDisp } from '@/lib/dateHelper';
import { IntervalID } from '@/lib/requestAnimationFrame';
import { useRoute } from '@/hooks/useRoute';

interface BoardUnreadNotificationState {
  isPanelExpanded: boolean;
  notifications: BoardUnreadNotificationResponse[];
  intervalId: IntervalID | null;
}

interface NotificationCard {
  notification: BoardUnreadNotificationResponse;
  hanLabel: string;
}

const POLLING_INTERVAL_MS = 60000;
const CONTENT_PREVIEW_MAX_LENGTH = 20;

// HTMLエンティティ（&amp; &lt; &gt; &quot; など）を実文字へデコードする。
// 表示は {{ }} 経由でVueがエスケープするため、デコード済みテキストでもXSSにはならない。
function decodeHtmlEntities(text: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

// HTMLを含みうる本文から1行目を抽出し、表示用にトリムする純粋関数。
function firstLinePreview(content: string | null): string {
  if (content === null) { return ''; }
  const stripped = content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ');
  const text = decodeHtmlEntities(stripped);
  const firstLine = text.split('\n').map(line => line.trim()).find(line => line.length > 0) ?? '';
  if (firstLine.length <= CONTENT_PREVIEW_MAX_LENGTH) { return firstLine; }
  return `${firstLine.slice(0, CONTENT_PREVIEW_MAX_LENGTH)}…`;
}

export default defineComponent({
  setup() {
    const state = reactive<BoardUnreadNotificationState>({
      isPanelExpanded: false,
      notifications: [],
      intervalId: null,
    });
    const { router } = useRoute();

    const totalUnreadCount = computed<number>(() => {
      return state.notifications.reduce((sum, notification) => sum + notification.unread_entries.length, 0);
    });

    const fetchNotifications = async(): Promise<void> => {
      try {
        const res = await boardEntryApi.getUnreadNotifications();
        state.notifications = res.data;
      } catch (e) {
        // 取得失敗時は次回ポーリング（60秒後）で再試行する
      }
    };

    const togglePanel = (): void => {
      state.isPanelExpanded = !state.isPanelExpanded;
    };

    const cardTimeLabel = (notification: BoardUnreadNotificationResponse): string => {
      const now = new Date();
      const entries = notification.unread_entries;
      if (entries.length === 0) return '';

      // entriesはバックエンドでcreated_at descソート済みのため、created_atを表示すれば並び順と一致する。
      const newest = fuzzyTimeDisp(entries[0].created_at, now);
      if (entries.length === 1) return newest;

      const oldest = fuzzyTimeDisp(entries[entries.length - 1].created_at, now);
      return `${newest} 〜 ${oldest}`;
    };

    const cardHanLabel = (notification: BoardUnreadNotificationResponse): string => {
      const entries = notification.unread_entries;
      if (entries.length === 0) return '';

      const newestHan = entries[0].han_disp;
      if (newestHan === null) return '';

      if (entries.length === 1) return newestHan;

      return `${newestHan}、他`;
    };

    const cardContentLabel = (notification: BoardUnreadNotificationResponse): string => {
      const entries = notification.unread_entries;
      if (entries.length === 0) return '';

      // 内容が空の場合は連絡事項（reply）の内容を表示する。
      const contentPreview = firstLinePreview(entries[0].content);
      if (contentPreview !== '') return contentPreview;

      return firstLinePreview(entries[0].reply);
    };

    const otherCount = (notification: BoardUnreadNotificationResponse): number => {
      return notification.unread_entries.length - 1;
    };

    const notificationCards = computed<NotificationCard[]>(() =>
      state.notifications.map(notification => ({
        notification,
        hanLabel: cardHanLabel(notification),
      })),
    );

    const onClickCard = async(notification: BoardUnreadNotificationResponse): Promise<void> => {
      const entries = notification.unread_entries;
      if (entries.length === 0) return;

      const latestEntryId = entries[0].id;
      // 同じカードを再度押した時に同一URLとなり遷移が起きないため、requested_at を付与する
      await router.push({
        name: 'SettouMessageBoard',
        query: {
          board_master_id: String(notification.board_master_id),
          latest_entry_id: String(latestEntryId),
          requested_at: String(Date.now()),
        },
      });
    };

    onMounted(() => {
      fetchNotifications();
      state.intervalId = window.requestInterval(() => fetchNotifications(), POLLING_INTERVAL_MS);
    });
    onUnmounted(() => {
      if (state.intervalId !== null) {
        window.clearRequestInterval(state.intervalId);
      }
    });

    return {
      state,
      totalUnreadCount,
      notificationCards,
      togglePanel,
      cardTimeLabel,
      cardContentLabel,
      otherCount,
      onClickCard,
    };
  },
});
